import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';

import { AuthenticationService } from '../../authentication/authentication.service';
import { UpgradeLoggerRepository } from '../repositories';
import {
  SessionCreateData,
  EventCreateData,
  DeviceSnapshot,
  ModuleUpgradeInfo,
  ErrorInfo,
  FirmwareTargetInfo
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class UpgradeLoggerService {
  private readonly currentSessionId$ = new BehaviorSubject<string | null>(null);
  private readonly currentDeviceSerial$ = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly repository: UpgradeLoggerRepository,
    private readonly auth: AuthenticationService
  ) {}

  /**
   * Start a new upgrade session
   */
  async startSession(
    deviceSerial: string,
    deviceModel: string,
    modules: string[],
    preUpgradeSnapshot: DeviceSnapshot,
    fwItemsTarget?: FirmwareTargetInfo[]
  ): Promise<string> {
    const user = await this.auth.user$.pipe(first()).toPromise();
    if (!user) {
      throw new Error('Authentication required for upgrade logging');
    }

    const moduleInfos: ModuleUpgradeInfo[] = modules.map(name => ({
      name,
      status: 'pending'
    }));

    const sessionData: SessionCreateData = {
      deviceSerial,
      deviceModel,
      operatorId: user.uid,
      operatorName: user.displayName || 'Unknown Operator',
      operatorEmail: user.email || 'unknown@domain.com',
      modules: moduleInfos,
      preUpgradeSnapshot,
      fwItemsTarget
    };

    const sessionId = await this.repository.createSession(deviceSerial, sessionData);

    this.currentSessionId$.next(sessionId);
    this.currentDeviceSerial$.next(deviceSerial);

    // Log session start event
    await this.logEvent({
      operation: 'start',
      phase: 'Session Started'
    });

    return sessionId;
  }

  /**
   * Log an upgrade event
   */
  async logEvent(eventData: EventCreateData): Promise<void> {
    const sessionId = this.currentSessionId$.value;
    const deviceSerial = this.currentDeviceSerial$.value;

    if (!sessionId || !deviceSerial) {
      console.warn('No active upgrade session for logging event');
      return;
    }

    try {
      await this.repository.logEvent(deviceSerial, sessionId, eventData);
    } catch (error) {
      console.error('Failed to log upgrade event:', error);
      // Non-blocking: upgrade should continue even if logging fails
    }
  }


  /**
   * Log error
   */
  async logError(error: ErrorInfo, moduleId?: string): Promise<void> {
    await this.logEvent({
      operation: 'error',
      moduleId,
      error,
      phase: `Error: ${error.code}`
    });
  }


  /**
   * Complete the current session with post-upgrade snapshot
   */
  async completeSession(postUpgradeSnapshot: DeviceSnapshot): Promise<void> {
    const sessionId = this.currentSessionId$.value;
    const deviceSerial = this.currentDeviceSerial$.value;

    if (!sessionId || !deviceSerial) {
      console.warn('No active upgrade session to complete');
      return;
    }

    try {
      // Log completion event
      await this.logEvent({
        operation: 'complete',
        phase: 'Session completed'
      });

      // Update session with post-upgrade snapshot and completion status
      await this.repository.updateSessionStatus(deviceSerial, sessionId, 'completed');
      await this.repository.updateSessionEndTime(deviceSerial, sessionId, Date.now());
      await this.repository.updatePostUpgradeSnapshot(deviceSerial, sessionId, postUpgradeSnapshot);

      // Clear current session
      this.currentSessionId$.next(null);
      this.currentDeviceSerial$.next(null);
    } catch (error) {
      console.error('Failed to complete upgrade session:', error);
    }
  }

  /**
   * Cancel the current session
   */
  async cancelSession(): Promise<void> {
    const sessionId = this.currentSessionId$.value;
    const deviceSerial = this.currentDeviceSerial$.value;

    if (!sessionId || !deviceSerial) {
      console.warn('No active upgrade session to cancel');
      return;
    }

    try {
      // Log cancellation event
      await this.logEvent({
        operation: 'cancelled',
        phase: 'Session cancelled'
      });

      // Update session status and end time
      await this.repository.updateSessionStatus(deviceSerial, sessionId, 'cancelled');
      await this.repository.updateSessionEndTime(deviceSerial, sessionId, Date.now());

      // Clear current session
      this.currentSessionId$.next(null);
      this.currentDeviceSerial$.next(null);
    } catch (error) {
      console.error('Failed to cancel upgrade session:', error);
    }
  }

  /**
   * Mark session as failed
   */
  async failSession(): Promise<void> {
    const sessionId = this.currentSessionId$.value;
    const deviceSerial = this.currentDeviceSerial$.value;

    if (!sessionId || !deviceSerial) {
      console.warn('No active upgrade session to fail');
      return;
    }

    try {
      // Log failure event
      await this.logEvent({
        operation: 'failed',
        phase: 'Session failed'
      });

      // Update session status and end time
      await this.repository.updateSessionStatus(deviceSerial, sessionId, 'failed');
      await this.repository.updateSessionEndTime(deviceSerial, sessionId, Date.now());

      // Clear current session
      this.currentSessionId$.next(null);
      this.currentDeviceSerial$.next(null);
    } catch (error) {
      console.error('Failed to mark upgrade session as failed:', error);
    }
  }
}
